import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, Clock, AlertTriangle, Eye, Send, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { useAppraisals, useTemplates, useNotifications } from '../../../hooks/useDatabase';
import { formatDate, getDaysUntil, isOverdue } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor, getScoreColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';

export default function TeamAppraisalsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { createAppraisal, loading: appraisalLoading } = useAppraisals();
  const { createNotification } = useNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamMembers, setTeamMembers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [assignData, setAssignData] = useState({ templateId: '', dueDate: '', cycleDays: 'none', autoReminders: false, reminderLeadDays: 'none' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  useEffect(() => { loadData(); }, [user]);

  useEffect(() => {
    if (!appraisals.length || !teamMembers.length) return;
    const checkAndSendReminders = async () => {
      const now = Date.now();
      const pendingReminders = appraisals.filter(a => {
        if (!a.autoReminders) return false;
        if (a.remindersSent) return false;
        const lead = typeof a.reminderLeadDays === 'number' ? a.reminderLeadDays : Number(a.reminderLeadDays || 0);
        if (!lead) return false;
        if (!a.dueDate) return false;
        const msUntilDue = a.dueDate - now;
        return msUntilDue > 0 && msUntilDue <= lead * 24 * 60 * 60 * 1000;
      });

      if (pendingReminders.length === 0) return;

      for (const appraisal of pendingReminders) {
        const targets = new Set([appraisal.userId]);
        if (user?.id) targets.add(user.id);

        try {
          await Promise.all(Array.from(targets).map(userId => createNotification({
            userId,
            type: 'appraisal_reminder',
            title: 'Appraisal due soon',
            message: `${appraisal.title} is due on ${formatDate(appraisal.dueDate)}. Please complete it.`
          })));
          await db.appraisals.update(appraisal.id, { remindersSent: true });
          setAppraisals(prev => prev.map(a => a.id === appraisal.id ? { ...a, remindersSent: true } : a));
        } catch (error) {
          console.error('Failed to send reminder', error);
        }
      }
    };

    checkAndSendReminders();
    const interval = setInterval(checkAndSendReminders, 60 * 1000);
    return () => clearInterval(interval);
  }, [appraisals, teamMembers, createNotification, user]);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData, templatesData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray(),
        db.templates.toArray()
      ]);
      const team = usersData.filter(u => u.supervisorId === user?.id && u.status === 'active');
      setTeamMembers(team);
      // Only show appraisals where the current user is the assigned manager AND the employee is in their team
      setAppraisals(appraisalsData.filter(a => 
        a.managerId === user?.id && 
        team.some(m => m.id === a.userId) && 
        a.visibleToSupervisor !== false
      ));
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserById = (id) => teamMembers.find(u => u.id === id);

  const filteredAppraisals = appraisals.filter(appraisal => {
    const employee = getUserById(appraisal.userId);
    const matchesSearch = searchQuery === '' ||
      employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appraisal.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || appraisal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const departments = [...new Set(teamMembers.map(e => e.profile?.department).filter(Boolean))];

  const filteredSelectableEmployees = teamMembers.filter(emp => {
    const matchesSearch = employeeSearch === '' ||
      emp.profile?.firstName?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.profile?.lastName?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.email?.toLowerCase().includes(employeeSearch.toLowerCase());
    const matchesDept = departmentFilter === 'all' || emp.profile?.department === departmentFilter;
    return matchesSearch && matchesDept;
  });

  const stats = {
    pending: appraisals.filter(a => a.status === 'employee_completed').length,
    inProgress: appraisals.filter(a => a.status === 'ready_to_evaluate').length,
    completed: appraisals.filter(a => a.status === 'finalized').length,
    overdue: appraisals.filter(a => isOverdue(a.dueDate) && a.status !== 'finalized').length
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployees(prev => prev.includes(employeeId)
      ? prev.filter(id => id !== employeeId)
      : [...prev, employeeId]);
  };

  const selectAllFilteredEmployees = () => {
    setSelectedEmployees(filteredSelectableEmployees.map(emp => emp.id));
  };

  const sendReminder = async (appraisal) => {
    try {
      const employee = getUserById(appraisal.userId);
      await createNotification({
        userId: appraisal.userId,
        type: 'appraisal_reminder',
        title: 'Appraisal Reminder',
        message: `Reminder to complete ${appraisal.title}`,
        read: false,
        createdAt: Date.now(),
        link: `/appraisal/${appraisal.id}`
      });
      toast.success(`Reminder sent to ${employee?.profile?.firstName || 'employee'}`);
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleAssignAppraisal = async () => {
    if (!assignData.templateId || !assignData.dueDate || selectedEmployees.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    const template = templates.find(t => t.id === assignData.templateId);
    const performanceAreas = template?.sections?.[0]?.competencies?.map(c => ({
      competencyId: c.id,
      competencyName: c.name,
      weight: c.weight,
      completedByEmployee: false,
      completedByManager: false
    })) || [];

    try {
      for (const empId of selectedEmployees) {
        const emp = getUserById(empId);
        await createAppraisal({
          templateId: assignData.templateId,
          userId: empId,
          managerId: user?.id,
          title: `${template?.name || 'Performance Review'} - ${emp?.profile?.firstName} ${emp?.profile?.lastName}`,
          type: template?.type || 'annual',
          dueDate: new Date(assignData.dueDate).getTime(),
          performanceAreas,
          cycleDays: assignData.cycleDays && assignData.cycleDays !== 'none' ? Number(assignData.cycleDays) : null,
          autoReminders: assignData.autoReminders,
          reminderLeadDays: assignData.autoReminders && assignData.reminderLeadDays && assignData.reminderLeadDays !== 'none'
            ? Number(assignData.reminderLeadDays)
            : null,
          remindersSent: false
        });
      }
      toast.success(`Appraisal assigned to ${selectedEmployees.length} employee(s)`);
      setShowAssignModal(false);
      setSelectedEmployees([]);
      setAssignData({ templateId: '', dueDate: '', cycleDays: 'none', autoReminders: false, reminderLeadDays: 'none' });
      loadData();
    } catch (error) {
      toast.error('Failed to assign appraisal');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Appraisals</h1>
          <p className="text-gray-500 mt-1">Review and manage your team's performance evaluations</p>
        </div>
        <Button onClick={() => setShowAssignModal(true)} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Assign Appraisal
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.pending}</p><p className="text-xs text-gray-500">Pending Review</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center"><Eye className="w-5 h-5 text-violet-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p><p className="text-xs text-gray-500">In Progress</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.completed}</p><p className="text-xs text-gray-500">Completed</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.overdue}</p><p className="text-xs text-gray-500">Overdue</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search by employee or title..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ready_to_evaluate">In Progress</SelectItem>
                <SelectItem value="employee_completed">Pending Review</SelectItem>
                <SelectItem value="finalized">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredAppraisals.map(appraisal => {
          const employee = getUserById(appraisal.userId);
          const daysUntil = getDaysUntil(appraisal.dueDate);
          const overdue = isOverdue(appraisal.dueDate) && appraisal.status !== 'finalized';
          const needsReview = appraisal.status === 'employee_completed';

          return (
            <Card key={appraisal.id} className={`border-0 shadow-sm hover:shadow-md transition-shadow ${needsReview ? 'border-l-4 border-l-amber-500' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {employee?.profile?.firstName?.[0]}{employee?.profile?.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{employee?.profile?.firstName} {employee?.profile?.lastName}</h3>
                      <p className="text-sm text-gray-500">{appraisal.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
                        <span className={`text-xs ${overdue ? 'text-rose-600' : 'text-gray-400'}`}>
                          {overdue ? `Overdue by ${Math.abs(daysUntil)} days` : `Due in ${daysUntil} days`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="w-32">
                      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{appraisal.progress?.overallPercent || 0}%</span></div>
                      <Progress value={appraisal.progress?.overallPercent || 0} className="h-2" />
                    </div>
                    {appraisal.scores?.employeeSelfTotal && (
                      <div className="text-center"><p className="text-xs text-gray-500">Self Score</p><p className={`text-lg font-bold ${getScoreColor(appraisal.scores.employeeSelfTotal)}`}>{appraisal.scores.employeeSelfTotal.toFixed(1)}</p></div>
                    )}
                    <div className="flex items-center gap-2">
                      {appraisal.status === 'ready_to_evaluate' && (
                        <Button variant="outline" size="sm" onClick={() => sendReminder(appraisal)}><Send className="w-4 h-4 mr-1" /> Remind</Button>
                      )}
                      <Button
                        size="sm"
                        className={needsReview ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}
                        variant={needsReview ? 'default' : 'outline'}
                        onClick={() => navigate(`/supervisor/appraisals/${appraisal.id}`)}
                      >
                        {needsReview ? 'Review Now' : 'View'}
                      </Button>
                    </div>
                  </div>
                </div>
                <AIAnalysisSummary appraisal={appraisal} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredAppraisals.length === 0 && <div className="text-center py-12"><p className="text-gray-500">No appraisals found</p></div>}

      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-3xl h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Appraisal</DialogTitle>
            <DialogDescription>Select team members and template</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template *</Label>
              <Select value={assignData.templateId} onValueChange={(v) => setAssignData({...assignData, templateId: v})}>
                <SelectTrigger><SelectValue placeholder="Select template..." /></SelectTrigger>
                <SelectContent>{templates.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input type="date" value={assignData.dueDate} onChange={(e) => setAssignData({...assignData, dueDate: e.target.value})} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="h-6 flex items-center">
                  <Label>Cycle (optional)</Label>
                </div>
                <Select value={assignData.cycleDays} onValueChange={(v) => setAssignData({ ...assignData, cycleDays: v })}>
                  <SelectTrigger><SelectValue placeholder="No cycle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No cycle</SelectItem>
                    <SelectItem value="7">Every 7 days</SelectItem>
                    <SelectItem value="15">Every 15 days</SelectItem>
                    <SelectItem value="30">Every 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="h-6 flex items-center gap-2">
                  <Checkbox
                    checked={assignData.autoReminders}
                    onCheckedChange={(checked) => setAssignData({ ...assignData, autoReminders: !!checked })}
                  />
                  <Label>Auto reminders</Label>
                </div>
                <Select
                  value={assignData.reminderLeadDays}
                  onValueChange={(v) => setAssignData({ ...assignData, reminderLeadDays: v })}
                  disabled={!assignData.autoReminders}
                >
                  <SelectTrigger><SelectValue placeholder="Choose lead time" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No reminders</SelectItem>
                    <SelectItem value="1">1 day before</SelectItem>
                    <SelectItem value="3">3 days before</SelectItem>
                    <SelectItem value="5">5 days before</SelectItem>
                    <SelectItem value="7">7 days before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Select Team Members *</Label>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search team by name or email"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="md:w-44"><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    className="md:w-40"
                    onClick={selectAllFilteredEmployees}
                    disabled={filteredSelectableEmployees.length === 0}
                  >
                    Select All Filtered
                  </Button>
                </div>

                <div className="border rounded-lg max-h-52 overflow-y-auto">
                  {filteredSelectableEmployees.map(member => (
                    <div key={member.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                      <Checkbox checked={selectedEmployees.includes(member.id)} onCheckedChange={() => toggleEmployee(member.id)} />
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">
                        {member.profile?.firstName?.[0]}{member.profile?.lastName?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{member.profile?.firstName} {member.profile?.lastName}</p>
                        <p className="text-xs text-gray-500">{member.profile?.department} • {member.role}</p>
                      </div>
                    </div>
                  ))}
                  {filteredSelectableEmployees.length === 0 && (
                    <div className="p-3 text-sm text-gray-500 text-center">No users match the current filters</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">{selectedEmployees.length} team member(s) selected</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" disabled={appraisalLoading || selectedEmployees.length === 0} onClick={handleAssignAppraisal}>
              {appraisalLoading ? 'Assigning...' : 'Assign Appraisal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
